import { useEffect } from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { onFirstInteraction } from '@/lib/interaction';

/**
 * Lenis smooth scroll — the single change with the largest effect on
 * perceived quality (docs/DESIGN-V7.md §9).
 *
 * **Lenis and GSAP are imported dynamically, and that is a performance
 * decision, not a style one.** This component is eager chrome, so a static
 * import pulled ~50 KB gz of GSAP plus Lenis into the initial bundle — which
 * a client-rendered page must parse before it can paint anything at all.
 * Measured on simulated mobile: that pushed FCP to 2.2s and LCP to 3.4s.
 * Everything else that needs GSAP lives in a lazy section chunk, so deferring
 * it here takes it off the critical path entirely and the chunks still share.
 *
 * It also waits for first interaction: a visitor who has not scrolled yet
 * cannot benefit from smooth scrolling, so there is nothing to gain by
 * loading it sooner.
 *
 * Driven from GSAP's ticker rather than its own rAF loop. Two independent
 * loops let Lenis move the page after ScrollTrigger has already measured for
 * that frame, which shows up as scrubbed animations lagging a frame behind.
 *
 * Gated off entirely for touch and reduced motion (CLAUDE.md §11) — those
 * visitors get native scrolling, which is correct behaviour, not degraded.
 */
export function SmoothScroll() {
  const finePointer = useMediaQuery('(pointer: fine)');
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!finePointer || reduced) return;

    let cancelled = false;
    let teardown: (() => void) | null = null;

    const start = () => {
      void Promise.all([import('lenis'), import('@/lib/gsap')]).then(
        ([{ default: Lenis }, { gsap, ScrollTrigger }]) => {
          if (cancelled) return;

          // `anchors` lets Lenis own hash-link navigation. Without it, a plain
          // `<a href="#work">` jumps natively while Lenis keeps its own
          // position state, and the two disagree until the next wheel event.
          const lenis = new Lenis({ duration: 1.1, smoothWheel: true, anchors: true });
          document.documentElement.classList.add('lenis');
          lenis.on('scroll', ScrollTrigger.update);

          const raf = (time: number) => lenis.raf(time * 1000);
          gsap.ticker.add(raf);
          // Lag smoothing lets GSAP skip time after a long frame, which
          // desynchronises Lenis's own position integration.
          gsap.ticker.lagSmoothing(0);

          teardown = () => {
            gsap.ticker.remove(raf);
            gsap.ticker.lagSmoothing(500, 33);
            lenis.destroy();
            document.documentElement.classList.remove('lenis');
          };
        },
      );
    };

    const cancelGate = onFirstInteraction(start);

    return () => {
      cancelled = true;
      cancelGate();
      teardown?.();
    };
  }, [finePointer, reduced]);

  return null;
}
